import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@abvehicles/routes/Routes';
import { useState } from 'react';
import { ErrorMessages } from '@types';
import { useAbVehiclesStore } from '@abvehicles/store/store';
import { IssueType } from '@abvehicles/types/issue-types';
import { BinaryQuestionTemplate } from '@templates';
import { ZodError } from 'zod';
import {
  OPTIONS_REQUIRED_ERROR,
  parseZodErrors,
  stringFieldSchema,
  TEXT_AREA_REQUIRED_ERROR,
  TextAreaMaxLengthSchema,
} from '@utils';
import { AdditionalQuestion } from './AdditionalQuestion';
import {
  issueTypeQuestionData,
  issueTypeTextAreaData,
} from '@abvehicles/constants/questionData';

export const IssueTypePage = () => {
  const navigate = useNavigate();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const { issueType, updateIssueType } = useAbVehiclesStore();
  const questionData = issueTypeQuestionData;
  const textAreaErrorPath = TEXT_AREA_REQUIRED_ERROR.required.path;

  const options = Object.keys(IssueType).map((issueKey) => {
    return {
      id: issueKey,
      name: issueKey,
      labelText: IssueType[issueKey as keyof typeof IssueType],
      labelTextStyle: '!font-normal',
      isLabelBold: false,
      subtext: '',
      buttonRef: null,
      isDefault:
        issueType.issueType === IssueType[issueKey as keyof typeof IssueType],
    };
  });

  const handleChange = (result: string) => {
    setErrorMessages([]);
    updateIssueType({
      additionalInfo: '',
      issueType: result as IssueType,
    });
  };

  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.UPLOAD_PHOTO);
    },
  };

  const buttonNext = {
    onClick: () => {
      try {
        const validatedData = stringFieldSchema(
          questionData.errorPath,
          OPTIONS_REQUIRED_ERROR.required.message,
        ).parse({
          [questionData.errorPath]: issueType.issueType || '',
        });
        updateIssueType({
          ...issueType,
          issueType: validatedData[questionData.errorPath] as IssueType,
        });

        if (
          validatedData[questionData.errorPath] === IssueType.NONE_OF_THE_ABOVE
        ) {
          const validateTextAreaData = stringFieldSchema(
            textAreaErrorPath,
            TEXT_AREA_REQUIRED_ERROR.required.message,
          ).parse({
            [textAreaErrorPath]: issueType.additionalInfo || '',
          });

          TextAreaMaxLengthSchema({
            fieldNames: [textAreaErrorPath],
            maxLength: issueTypeTextAreaData.maxLength,
          }).parse({
            [textAreaErrorPath]: issueType.additionalInfo,
          });

          updateIssueType({
            ...issueType,
            additionalInfo: validateTextAreaData[textAreaErrorPath],
          });
        }
        navigate(RoutesList.HOW_LONG);
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error) => ({
            message: error.message,
            path: error.path,
          })),
        );
      }
    },
  };

  return (
    <BinaryQuestionTemplate
      title={questionData.title}
      description={questionData.description}
      errorPath={questionData.errorPath}
      errorMessages={errorMessages}
      onChange={handleChange}
      options={options}
      children={
        <AdditionalQuestion
          errorPath={textAreaErrorPath}
          errorMessages={errorMessages}
          selectedIssueType={issueType.issueType}
          setErrorMessages={setErrorMessages}
        />
      }
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
    />
  );
};
